<script setup lang="ts">
/**
 * A comment thread: the comments as `NxCommentItem` rows (edit, delete,
 * resolve on each), a loading and an empty state, and a composer with
 * @-mentions (`NxMentionTextarea`) under — or with `composerPosition="top"`
 * above — the list.
 *
 * The caller owns the data and the saving. `submit(draft)` hands over the
 * trimmed text with its mentions and empties the composer; if the save fails,
 * put the text back with the exposed `restore(draft)`. Row events (`edit`,
 * `delete`, `resolve`, `mention`) pass through from the rows.
 *
 * `currentUser` shows the viewer's avatar next to the composer; `canComment`
 * off hides it (read-only thread). `#item` replaces a row, `#header` sits on
 * top, `#empty` replaces the empty state.
 *
 * Selectable threads — a list of threads tied to places in a document: pass
 * `selected` (true or false). The thread then is a tab stop, shows an accent
 * edge and `aria-current="true"` while selected, and, while not selected,
 * emits `select` when it is clicked or focus moves into it from outside — the
 * inner controls keep working as before. Without `selected` nothing changes.
 *
 * `composerMode="collapsed"` shows an „Antworten“ button in place of the
 * composer until it is clicked, then the composer, focused. It folds back after
 * a send, on Escape in an empty composer, and when the mode is set to
 * `collapsed` again; a started draft keeps it open (blank text counts as empty
 * and is dropped on folding). Folding with the focus in the composer hands it
 * to the button, which stays focusable while `busy`. Switched to `open` while
 * the button has focus, the focus moves into the composer. Default `open`: the
 * composer is always there.
 */
import { computed, inject, nextTick, ref, useTemplateRef, watch } from 'vue';
import MessageSquare from 'lucide-vue-next/dist/esm/icons/message-square.js'; import Reply from 'lucide-vue-next/dist/esm/icons/reply.js'; import Send from 'lucide-vue-next/dist/esm/icons/send.js';
import { i18nInjectionKey } from '../internal';
import NxAvatar from '../Avatar/NxAvatar.vue';
import NxButton from '../Button/NxButton.vue';
import type { NxMention, NxMentionCandidate } from '../Mentions/types';
import NxCommentItem from './NxCommentItem.vue';
import NxMentionTextarea from './NxMentionTextarea.vue';
import type { NxCommentLabels } from './labels';
import type { NxComment, NxCommentAuthor, NxCommentDraft } from './types';

const props = withDefaults(
  defineProps<{
    comments?: readonly NxComment[];
    /** People for @-mentions in the composer and while editing. */
    candidates?: readonly NxMentionCandidate[];
    /** The viewer; shows their avatar at the composer. */
    currentUser?: NxCommentAuthor;
    loading?: boolean;
    /** A save is running: composer and rows wait. */
    busy?: boolean;
    /** Show the composer. */
    canComment?: boolean;
    resolvable?: boolean;
    confirmDelete?: boolean;
    mentionsInteractive?: boolean;
    composerPosition?: 'top' | 'bottom';
    locale?: string;
    /** Accessible name of the thread. */
    label?: string;
    placeholder?: string;
    submitLabel?: string;
    composerHint?: string;
    loadingLabel?: string;
    emptyTitle?: string;
    emptyHint?: string;
    /** Texts of the rows. */
    itemLabels?: Partial<NxCommentLabels>;
    /**
     * The thread is the selected one of a list. Passing it (true or false)
     * makes the thread selectable: a tab stop that emits `select`.
     */
    selected?: boolean;
    /** `collapsed`: an „Antworten“ button until the composer is wanted. */
    composerMode?: 'open' | 'collapsed';
    /** Text of the „Antworten“ button of a collapsed composer. */
    replyLabel?: string;
  }>(),
  {
    comments: () => [],
    candidates: () => [],
    currentUser: undefined,
    loading: false,
    busy: false,
    canComment: true,
    resolvable: false,
    confirmDelete: true,
    mentionsInteractive: false,
    composerPosition: 'bottom',
    locale: undefined,
    label: 'Kommentare',
    placeholder: 'Kommentar schreiben … @ erwähnt jemanden',
    submitLabel: 'Kommentieren',
    composerHint: 'Strg+Enter sendet',
    loadingLabel: 'Kommentare werden geladen …',
    emptyTitle: 'Noch keine Kommentare',
    emptyHint: 'Schreib den ersten — mit @ holst du jemanden dazu.',
    itemLabels: undefined,
    // Kept undefined when absent, so a thread without it is not selectable.
    selected: undefined,
    composerMode: 'open',
    replyLabel: undefined,
  }
);

const emit = defineEmits<{
  submit: [draft: NxCommentDraft];
  edit: [id: NxComment['id'], draft: NxCommentDraft];
  delete: [id: NxComment['id']];
  resolve: [id: NxComment['id'], resolved: boolean];
  mention: [mention: NxMention];
  /** A selectable thread was clicked or focus moved into it. */
  select: [];
}>();

defineSlots<{
  header?(): unknown;
  item?(props: { comment: NxComment }): unknown;
  empty?(): unknown;
  /** Next to the send button, e.g. an attach button. */
  'composer-actions'?(): unknown;
}>();

const composer = ref<InstanceType<typeof NxMentionTextarea> | null>(null);
const composerBox = useTemplateRef<HTMLElement>('composer-box');
const root = useTemplateRef<HTMLElement>('root');
const replyButton = useTemplateRef<InstanceType<typeof NxButton>>('reply-button');
const draftBody = ref('');
const draftMentions = ref<NxMention[]>([]);
/** A collapsed composer was opened with its button. */
const expanded = ref(false);

const canSubmit = computed(() => !props.busy && !!draftBody.value.trim());
const showEmpty = computed(() => !props.loading && props.comments.length === 0);
const selectable = computed(() => props.selected !== undefined);
/** A draft with more than blank text; only that keeps a collapsed composer open. */
const hasDraft = computed(() => !!draftBody.value.trim());
const composerOpen = computed(
  () => props.composerMode === 'open' || expanded.value || hasDraft.value
);

const i18n = inject(i18nInjectionKey, null);
function tr(key: string, fallback: string): string {
  const full = `common.comments.${key}`;
  const text = i18n?.t(full);
  return text && text !== full ? text : fallback;
}
const replyText = computed(() => props.replyLabel ?? tr('reply', 'Antworten'));

watch(
  () => props.composerMode,
  (mode) => {
    if (mode === 'collapsed') {
      expanded.value = false;
      return;
    }
    // Opened while its button had focus (a tab onto „Antworten“ selected the
    // thread): the focus goes on into the composer instead of being dropped.
    const button: unknown = replyButton.value?.$el;
    if (button && button === document.activeElement) {
      void nextTick(() => composer.value?.focus());
    }
  }
);

// Every way the composer folds (send, Escape, the mode, an emptied draft) runs
// through here, before the DOM drops it: a blank draft goes, and focus inside
// the composer moves on to the „Antworten“ button instead of falling to the page.
watch(composerOpen, (open, wasOpen) => {
  if (open || !wasOpen) return;
  if (draftBody.value) clear();
  if (composerBox.value?.contains(document.activeElement)) {
    void nextTick(() => replyButton.value?.focus());
  }
});

function onFocusin(event: FocusEvent) {
  if (!selectable.value || props.selected) return;
  // Only focus coming in from outside selects; moving between the thread's own controls does not.
  const from = event.relatedTarget;
  if (from instanceof Node && root.value?.contains(from)) return;
  emit('select');
}

function onClick() {
  // A click on plain text moves no focus in Safari; the click selects then.
  if (selectable.value && !props.selected) emit('select');
}

function openComposer() {
  expanded.value = true;
  void nextTick(() => composer.value?.focus());
}

/** Folds a collapsed composer back; the `composerOpen` watcher moves the focus. */
function collapse() {
  expanded.value = false;
}

function onCancel() {
  if (!hasDraft.value) collapse();
}

function submit() {
  if (!canSubmit.value) return;
  const raw = draftBody.value;
  const body = raw.trim();
  const lead = raw.length - raw.trimStart().length;
  const mentions = draftMentions.value
    .map((m) => ({ ...m, start: m.start - lead, end: m.end - lead }))
    .filter((m) => m.start >= 0 && m.end <= body.length);
  emit('submit', { body, mentions });
  clear();
  collapse();
}

function clear() {
  draftBody.value = '';
  draftMentions.value = [];
}

defineExpose({
  /** Focuses the composer; a collapsed one opens first. */
  focus: () => {
    if (composerOpen.value) composer.value?.focus();
    else openComposer();
  },
  clear,
  /** Puts a draft back into the composer, e.g. after a failed save. */
  restore(draft: NxCommentDraft) {
    draftMentions.value = draft.mentions.map((m) => ({ ...m }));
    draftBody.value = draft.body;
    expanded.value = true;
    void nextTick(() => composer.value?.focus());
  },
});
</script>

<template>
  <section
    ref="root"
    class="nx-comment-thread"
    :class="[
      `is-composer-${composerPosition}`,
      { 'is-selectable': selectable, 'is-selected': selected === true },
    ]"
    :aria-label="label"
    :aria-busy="loading || undefined"
    :aria-current="selected ? 'true' : undefined"
    :tabindex="selectable ? 0 : undefined"
    data-testid="nx-comment-thread"
    @focusin="onFocusin"
    @click="onClick"
  >
    <slot name="header" />

    <ol v-if="comments.length" class="nx-comment-thread__list">
      <li v-for="comment in comments" :key="comment.id" class="nx-comment-thread__entry">
        <slot name="item" :comment="comment">
          <NxCommentItem
            :comment="comment"
            :candidates="candidates"
            :resolvable="resolvable"
            :confirm-delete="confirmDelete"
            :busy="busy"
            :mentions-interactive="mentionsInteractive"
            :labels="itemLabels"
            :locale="locale"
            @edit="(id, draft) => emit('edit', id, draft)"
            @delete="(id) => emit('delete', id)"
            @resolve="(id, resolved) => emit('resolve', id, resolved)"
            @mention="(m) => emit('mention', m)"
          />
        </slot>
      </li>
    </ol>
    <p v-else-if="loading" class="nx-comment-thread__loading" role="status">
      <span class="nx-comment-thread__skeleton" aria-hidden="true" />
      <span class="nx-comment-thread__skeleton is-short" aria-hidden="true" />
      <span class="nx-comment-thread__sr">{{ loadingLabel }}</span>
    </p>
    <div v-else-if="showEmpty" class="nx-comment-thread__empty">
      <slot name="empty">
        <span class="nx-comment-thread__empty-icon" aria-hidden="true">
          <MessageSquare class="nxt-icon-md" />
        </span>
        <p class="nx-comment-thread__empty-title">{{ emptyTitle }}</p>
        <p v-if="canComment" class="nx-comment-thread__empty-hint">{{ emptyHint }}</p>
      </slot>
    </div>

    <div v-if="canComment && !composerOpen" class="nx-comment-thread__reply">
      <NxButton
        ref="reply-button"
        variant="ghost"
        :loading="busy"
        data-testid="nx-comment-reply"
        @click="openComposer"
      >
        <Reply class="nxt-icon-sm" aria-hidden="true" />
        {{ replyText }}
      </NxButton>
    </div>
    <div v-else-if="canComment" ref="composer-box" class="nx-comment-thread__composer">
      <NxAvatar
        v-if="currentUser"
        class="nx-comment-thread__me"
        :name="currentUser.name"
        :src="currentUser.src"
        :initials="currentUser.initials"
        :size="32"
        decorative
      />
      <div class="nx-comment-thread__compose">
        <NxMentionTextarea
          ref="composer"
          v-model="draftBody"
          v-model:mentions="draftMentions"
          :candidates="candidates"
          :placeholder="placeholder"
          :label="label"
          :disabled="busy"
          :picker-placement="composerPosition === 'bottom' ? 'above' : 'below'"
          @submit="submit"
          @cancel="onCancel"
        />
        <div class="nx-comment-thread__bar">
          <span class="nx-comment-thread__hint">{{ composerHint }}</span>
          <slot name="composer-actions" />
          <NxButton :disabled="!canSubmit" data-testid="nx-comment-submit" @click="submit">
            <Send class="nxt-icon-sm" aria-hidden="true" />
            {{ submitLabel }}
          </NxButton>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.nx-comment-thread {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-sm);
  min-width: 0;
}
.nx-comment-thread.is-composer-top .nx-comment-thread__composer,
.nx-comment-thread.is-composer-top .nx-comment-thread__reply {
  order: -1;
}
/* A selectable thread keeps room for the accent edge, so selecting never shifts it. */
.nx-comment-thread.is-selectable {
  padding-inline-start: var(--nxt-space-xs);
  border-inline-start: calc(var(--nxt-hairline) * 3) solid transparent;
  border-radius: var(--nxt-radius-sm);
  outline: none;
}
.nx-comment-thread.is-selected {
  border-inline-start-color: var(--nxt-brand-bg);
}
.nx-comment-thread.is-selectable:focus-visible {
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-comment-thread__list {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: inherit;
}
.nx-comment-thread__entry {
  position: relative;
}
/* A quiet rail between consecutive avatars ties the rows into one thread. */
.nx-comment-thread__entry + .nx-comment-thread__entry::before {
  content: '';
  position: absolute;
  inset-block-start: calc(var(--nxt-space-2xs) * -1 - var(--nxt-space-xs));
  inset-inline-start: calc(var(--nxt-space-xs) + var(--nxt-avatar-size-md) / 2);
  height: calc(var(--nxt-space-xs) * 2 + var(--nxt-space-2xs));
  border-inline-start: var(--nxt-hairline) solid var(--nxt-border-subtle);
  pointer-events: none;
}

.nx-comment-thread__loading {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  margin: 0;
  padding: var(--nxt-space-xs);
}
.nx-comment-thread__skeleton {
  display: block;
  height: calc(var(--nxt-size-unit) * 0.75);
  border-radius: var(--nxt-radius-sm);
  background: linear-gradient(
    90deg,
    var(--nxt-surface-sunken-bg) 0%,
    color-mix(in srgb, var(--nxt-fg) 8%, var(--nxt-surface-sunken-bg)) 50%,
    var(--nxt-surface-sunken-bg) 100%
  );
  background-size: 200% 100%;
  animation: nx-comment-shimmer 1.4s ease-in-out infinite;
}
.nx-comment-thread__skeleton.is-short {
  width: 60%;
}

.nx-comment-thread__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding: var(--nxt-space-lg) var(--nxt-space-md);
  border: var(--nxt-hairline) dashed var(--nxt-border);
  border-radius: var(--nxt-radius-lg);
  text-align: center;
}
.nx-comment-thread__empty-icon {
  display: grid;
  place-items: center;
  width: var(--nxt-tile-size-sm);
  height: var(--nxt-tile-size-sm);
  margin-block-end: var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
}
.nx-comment-thread__empty-title,
.nx-comment-thread__empty-hint {
  margin: 0;
}
.nx-comment-thread__empty-title {
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg);
}
.nx-comment-thread__empty-hint {
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
}

.nx-comment-thread__reply {
  display: flex;
  padding-inline: var(--nxt-space-2xs);
}
.nx-comment-thread__composer {
  display: flex;
  align-items: flex-start;
  gap: var(--nxt-space-sm);
  padding: var(--nxt-space-xs);
}
.nx-comment-thread__me {
  flex: none;
  margin-block-start: calc(var(--nxt-space-2xs) / 2);
}
.nx-comment-thread__compose {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
}
.nx-comment-thread__bar {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
}
.nx-comment-thread__hint {
  flex: 1;
  min-width: 0;
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
}
.nx-comment-thread__sr {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  margin: calc(var(--nxt-hairline) * -1);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

@keyframes nx-comment-shimmer {
  from {
    background-position: 100% 0;
  }
  to {
    background-position: -100% 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-comment-thread__skeleton {
    animation: none;
  }
}
</style>
